import React from "react";
import { t } from "@/lib/i18n";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const beginGoogleAuth = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Landing() {
  const { lang, setLang } = useApp();

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text-primary)] relative overflow-hidden">
      {/* Grain overlay */}
      <div className="grain-overlay" aria-hidden="true"></div>

      <header className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[var(--accent)]"></span>
          <span className="font-serif text-2xl tracking-tight">FaithCycle</span>
        </div>
        <Select value={lang} onValueChange={setLang}>
          <SelectTrigger
            data-testid="landing-language-select"
            className="w-[80px] h-9 bg-transparent border-[var(--border)] rounded-full text-xs uppercase tracking-widest"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[var(--surface-elevated)]">
            <SelectItem value="en">EN</SelectItem>
            <SelectItem value="es">ES</SelectItem>
          </SelectContent>
        </Select>
      </header>

      <section className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 pt-10 md:pt-20 pb-24">
        <div className="md:col-span-7">
          <p className="uppercase tracking-[0.3em] text-xs text-[var(--text-secondary)] mb-6">
            KJV · WEB · NIV · NLT · RV 1960 · RVG · NTV · PDT · LBLA
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl leading-[1.05] tracking-tight text-[var(--text-primary)]">
            {t("landingH1", lang)}
          </h1>
          <p className="mt-8 text-[var(--text-secondary)] text-lg md:text-xl max-w-xl font-light leading-relaxed">
        {t("landingSub", lang)}
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Button
              data-testid="google-signin-btn"
              onClick={beginGoogleAuth}
              className="rounded-full h-12 px-8 bg-[var(--text-primary)] text-[var(--bg)] hover:bg-[var(--accent)] hover:text-white transition-colors duration-300 font-normal text-base"
            >
              <GoogleIcon /> {t("signInGoogle", lang)}
            </Button>
            <span className="text-xs text-[var(--text-secondary)] uppercase tracking-widest">
              {lang === "es" ? "Sin contraseñas · Solo Google" : "No passwords · Google only"}
            </span>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl">
            <FeatureTile n="01" title={lang === "es" ? "Lectura serena" : "Quiet reading"} />
            <FeatureTile n="02" title={lang === "es" ? "Reflexiones" : "Reflections"} />
            <FeatureTile n="03" title={lang === "es" ? "Amistades" : "Friends"} />
            <FeatureTile n="04" title={lang === "es" ? "Planes" : "Reading plans"} />
          </div>
        </div>

        <div className="md:col-span-5 md:pt-8 space-y-10">
          <PrayGrowShareCircle lang={lang} />
          <div className="relative rounded-3xl overflow-hidden border border-[var(--border)] bg-[var(--surface)]">
            <img
              src="https://images.unsplash.com/photo-1486707471592-8e7eb7e36f78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwbmF0dXJlJTIwbGFuZHNjYXBlJTIwbWlzdHxlbnwwfHx8fDE3ODU3MjA1MDh8MA&ixlib=rb-4.1.0&q=85"
              alt="misty forest"
              className="w-full h-[280px] object-cover"
            />
            <div className="absolute inset-0 bg-black/25"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <p className="uppercase tracking-[0.25em] text-base text-white/90 font-medium">
                {lang === "es" ? "SALMO 46:10" : "PSALM 46:10"}
              </p>
              <p
                className="font-serif text-white leading-snug mt-3"
                style={{ fontSize: "19px" }}
                data-testid="landing-verse"
              >
                {lang === "es"
                  ? "Estad quietos, y conoced que yo soy Dios."
                  : "Be still, and know that I am God."}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 mr-2" aria-hidden="true">
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.6 4.1-5.5 4.1a6.2 6.2 0 1 1 0-12.4c1.94 0 3.24.83 3.99 1.54l2.72-2.62A9.9 9.9 0 0 0 12 2 10 10 0 1 0 12 22c5.77 0 9.6-4.05 9.6-9.76 0-.66-.07-1.16-.16-1.66H12Z"/>
  </svg>
);

const FeatureTile = ({ n, title }) => (
  <div className="border-t border-[var(--border)] pt-4">
    <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-secondary)]">
      {n}
    </div>
    <div className="mt-2 text-sm">{title}</div>
  </div>
);

// Rotating cycle diagram: Pray → Grow → Share
const PrayGrowShareCircle = ({ lang }) => {
  const nodes = [
    { key: "pray", label: lang === "es" ? "Orar" : "Pray", sub: lang === "es" ? "Habla con Dios" : "Talk with God" },
    { key: "grow", label: lang === "es" ? "Crecer" : "Grow", sub: lang === "es" ? "Lee y estudia" : "Read and study" },
    { key: "share", label: lang === "es" ? "Compartir" : "Share", sub: lang === "es" ? "Con la comunidad" : "With the community" },
  ];
  // Positions on circle radius 130 around center (170, 170)
  const cx = 170, cy = 170, r = 120;
  const positions = nodes.map((_, i) => {
    const a = (-Math.PI / 2) + (i * 2 * Math.PI) / 3; // start at top, clockwise
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  });
  // Arc paths between nodes (clockwise, slightly inset from nodes)
  const nodeRadius = 40;
  const arcs = positions.map((p, i) => {
    const next = positions[(i + 1) % positions.length];
    // Vector from p to next, offset endpoints so arrows land at edge of circle nodes
    const dx = next.x - p.x, dy = next.y - p.y;
    const len = Math.hypot(dx, dy);
    const ux = dx / len, uy = dy / len;
    const startX = p.x + ux * nodeRadius;
    const startY = p.y + uy * nodeRadius;
    const endX = next.x - ux * (nodeRadius + 6);
    const endY = next.y - uy * (nodeRadius + 6);
    // Curved arc pushed outward from center
    const mx = (startX + endX) / 2;
    const my = (startY + endY) / 2;
    const outX = mx + (mx - cx) * 0.35;
    const outY = my + (my - cy) * 0.35;
    return `M ${startX} ${startY} Q ${outX} ${outY} ${endX} ${endY}`;
  });

  return (
    <div className="rounded-3xl border border-[var(--border)] bg-[var(--surface-elevated)] p-8">
      <p className="uppercase tracking-[0.3em] text-[10px] text-[var(--accent)] text-center">
        {lang === "es" ? "El círculo de la fe" : "The cycle of faith"}
      </p>
      <div className="relative mx-auto mt-4 w-[340px] h-[340px]">
        <svg viewBox="0 0 340 340" className="absolute inset-0 w-full h-full">
          <defs>
            <marker id="arrowhead" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--accent)" />
            </marker>
          </defs>
          <g style={{ transformOrigin: "170px 170px" }}>
            {arcs.map((d, i) => (
              <path key={i} d={d} fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" markerEnd="url(#arrowhead)" opacity="0.7" />
            ))}
          </g>
        </svg>
        {positions.map((p, i) => {
          const n = nodes[i];
          return (
            <div
              key={n.key}
              data-testid={`cycle-node-${n.key}`}
              className="absolute flex flex-col items-center justify-center rounded-full border border-[var(--accent)]/40 bg-[var(--bg)] shadow-sm"
              style={{
                width: 80,
                height: 80,
                left: p.x - 40,
                top: p.y - 40,
              }}
            >
              <span className="font-serif text-lg tracking-tight text-[var(--text-primary)]">
                {n.label}
              </span>
              <span className="text-[9px] uppercase tracking-widest text-[var(--text-secondary)] text-center px-2 leading-tight">
                {n.sub}
              </span>
            </div>
          );
        })}
        {/* Center dot */}
        <div
          className="absolute rounded-full bg-[var(--accent)] cycle-pulse"
          style={{ width: 8, height: 8, left: cx - 4, top: cy - 4 }}
        />
      </div>
    </div>
  );
};
